import { useState, useEffect, useCallback } from 'react';
import Modal from '../common/Modal';
import api from '../../services/api';
import { useToast } from '../../hooks/useToast';
import styles from './ReferralModal.module.css';

const ReferralModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [selectingPromo, setSelectingPromo] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchReferralData = useCallback(async () => {
    if (!isOpen) return;
    
    setLoading(true);
    try {
      const response = await api.get('/referrals/my-link');
      setData(response);
      setSelectedPromo(response.selectedPromoId || null);
    } catch (error) {
      toast.error('Failed to load referral data');
      console.error('Referral fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [isOpen, toast]);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  const handleSelectPromo = async (promoId) => {
    setSelectingPromo(true);
    try {
      await api.post('/referrals/select-promo', { promoCodeId: promoId });
      setSelectedPromo(promoId);
      toast.success('Reward preference saved');
    } catch (error) {
      toast.error('Failed to save preference');
    } finally {
      setSelectingPromo(false);
    }
  };

  const handleCopyLink = async () => {
    if (!data?.referralLink) return;
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(data.referralLink);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = data.referralLink;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const formatDiscount = (promo) => {
    if (promo.discountType === 'free_months') {
      const months = promo.freeMonths || 1;
      return `${months} month${months > 1 ? 's' : ''} free`;
    }
    if (promo.discountType === 'percentage') {
      return `${promo.discountValue}% off`;
    }
    return `$${(promo.discountValue / 100).toFixed(2)} off`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Refer a Friend" size="large">
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading your referral details...</p>
          </div>
        ) : !data ? (
          <div className={styles.errorState}>
            <p>Unable to load referral data. Please try again.</p>
            <button className={styles.retryBtn} onClick={fetchReferralData}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Referral Link Section */}
            <div className={styles.linkSection}>
              <h3 className={styles.sectionTitle}>Your Referral Link</h3>
              <p className={styles.sectionDesc}>
                Share this link with friends. When they sign up, you both get rewarded!
              </p>
              <div className={styles.linkBox}>
                <span className={styles.linkText}>{data.referralLink}</span>
                <button 
                  className={`${styles.copyBtn} ${copied ? styles.copied : ''}`}
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy Link
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Reward Selection Section */}
            {data.availableRewards?.length > 0 && (
              <div className={styles.promosSection}>
                <h3 className={styles.sectionTitle}>Choose Your Reward</h3>
                <p className={styles.sectionDesc}>
                  Select which reward your friends will receive when they sign up.
                </p>
                <div className={styles.promosGrid}>
                  {data.availableRewards.map((promo) => (
                    <div
                      key={promo.id}
                      className={`${styles.promoCard} ${selectedPromo === promo.id ? styles.selected : ''}`}
                      onClick={() => !selectingPromo && handleSelectPromo(promo.id)}
                    >
                      <div className={styles.promoDiscount}>{formatDiscount(promo)}</div>
                      <div className={styles.promoName}>{promo.code}</div>
                      {promo.description && (
                        <div className={styles.promoDesc}>{promo.description}</div>
                      )}
                      {selectedPromo === promo.id && (
                        <div className={styles.selectedBadge}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          Selected
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats Section */}
            <div className={styles.statsSection}>
              <h3 className={styles.sectionTitle}>Your Referral Stats</h3>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <span className={styles.statValue}>{data.stats?.total || 0}</span>
                  <span className={styles.statLabel}>Total Referrals</span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.green}`}>
                    {data.stats?.completed || 0}
                  </span>
                  <span className={styles.statLabel}>Successful Signups</span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.amber}`}>
                    {data.stats?.rewarded || 0}
                  </span>
                  <span className={styles.statLabel}>Rewards Earned</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ReferralModal;
